import React, { useEffect, useState, useCallback } from "react";
import "./LoanDetails.css";
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import { inputLableStyle, formControlStyle } from "../../../../calculatorStyle";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

const LoanDetails = ({ onCheck, onSelect, onData }) => {
  const [addOrginationOpen, setAddOrginationOpen] = useState(false);
  const [processingFeeOption, setProcessingFeeOption] = useState("percentage");

  const getDefaultStartDate = () => {
    const currentDate = new Date();
    currentDate.setDate(1);
    currentDate.setMonth(currentDate.getMonth() + 1);
    return currentDate.toISOString().split("T")[0];
  };
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [interestRate, setInterestRate] = useState(10);
  const [loanTenureMonths, setLoanTenureMonths] = useState(60);
  const [processingFeePercent, setProcessingFeePercent] = useState(1.5);
  const [processingFeeAmount, setProcessingFeeAmount] = useState(0);
  const [totalRepaymentAmount, setTotalRepaymentAmount] = useState(0);
  const [totalInterestPayable, setTotalInterestPayable] = useState(0);
  const [totalCostOfLoan, setTotalCostOfLoan] = useState("");
  const [fixedFee, setFixedFee] = useState();
  const [startDate, setStartDate] = useState(dayjs(getDefaultStartDate()));
  const [emi, setEmi] = useState(0);
  const [apr, setApr] = useState(0);

  const handleOrigination = () => {
    setAddOrginationOpen((prev) => !prev);
  };
  onCheck(addOrginationOpen);
  const handleProcessingFeeOpion = (event) => {
    setProcessingFeeOption(event.target.value);
  };
  onSelect(processingFeeOption);
  useEffect(() => {
    const fectchLoanCalculatorAPI = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_CALCULATOR_API}/personal_loan`
        );
        if (response && response.status === 200) {
          setInterestRate(response.data.data.persoanl_loan[0].interest_rate);
          setLoanTenureMonths(response.data.data.persoanl_loan[0].loan_tenure);
          setFixedFee(response.data.data.persoanl_loan[0].processing_fees);
        }
      } catch (error) {}
    };
    fectchLoanCalculatorAPI();
  }, []);
  // Loan Closure Date
  const calculateLoanClosureDate = useCallback(() => {
    const start = new Date(startDate);
    start.setDate(1);
    start.setMonth(start.getMonth() + 1);

    const closure = new Date(start);
    closure.setMonth(closure.getMonth() + parseInt(loanTenureMonths));
    const month = String(closure.getMonth() + 1).padStart(2, "0");
    const year = closure.getFullYear();
    return `${month}/${year}`;
  }, [startDate, loanTenureMonths]);

  const calculateAmortizationSchedule = useCallback(() => {
    const monthlyRate = interestRate / (12 * 100);
    let balance = loanAmount;
    const emiAmount =
      (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, loanTenureMonths)) /
      (Math.pow(1 + monthlyRate, loanTenureMonths) - 1);
    const schedule = [];
    let date = dayjs(startDate);
    for (let i = 1; i <= loanTenureMonths; i++) {
      const interest = balance * monthlyRate;
      const principal = emiAmount - interest;
      balance -= principal;
      schedule.push({
        month: i,
        date: date.format("MMM/YYYY").toUpperCase(),
        emi: formatter(Math.round(emiAmount)),
        principal: formatter(Math.round(principal)),
        interest: formatter(Math.round(interest)),
        balance: formatter(Math.round(balance)),
      });
      date = date.add(1, "month");
    }
    return schedule;
  }, [interestRate, loanAmount, loanTenureMonths, startDate]);

  useEffect(() => {
    const handleCalculate = () => {
      if (addOrginationOpen === false) {
        setFixedFee(0);
        setProcessingFeePercent(0);
      }
      // Calculate Processing Fee
      const processingFee =
        processingFeeOption === "percentage"
          ? (loanAmount * processingFeePercent) / 100
          : fixedFee;
      setProcessingFeeAmount(processingFee);

      // Calculate Net Loan Amount (Loan Amount - Processing Fee)
      const netLoanAmount = loanAmount - processingFee;

      // Calculate Monthly Interest Rate
      const monthlyRate = interestRate / (12 * 100);

      // Calculate EMI using the EMI formula
      const emiAmount =
        (loanAmount *
          monthlyRate *
          Math.pow(1 + monthlyRate, loanTenureMonths)) /
        (Math.pow(1 + monthlyRate, loanTenureMonths) - 1);
      setEmi(isNaN(emiAmount) ? 0 : Math.round(emiAmount));

      // Calculate Total Payable Amount
      const totalRepayment = emiAmount * loanTenureMonths;
      setTotalRepaymentAmount(
        isNaN(totalRepayment) ? 0 : Math.round(totalRepayment)
      );

      // Calculate Total Interest Payable
      const totalInterest = totalRepayment - loanAmount;
      setTotalInterestPayable(
        isNaN(totalInterest) ? 0 : Math.round(totalInterest)
      );

      const totalPayable = emiAmount * loanTenureMonths; // EMI * Tenure
      const costOfLoan = totalPayable - loanAmount + processingFee;

      setTotalCostOfLoan(isNaN(costOfLoan) ? 0 : Math.round(costOfLoan));

      // Calculate APR using iterative method
      const calculateAPR = (emi, netLoanAmount, tenure) => {
        let lowerRate = 0,
          upperRate = 1;
        let apr = 0;

        while (upperRate - lowerRate > 1e-6) {
          apr = (lowerRate + upperRate) / 2;
          const calculatedEMI =
            (netLoanAmount * apr * Math.pow(1 + apr, tenure)) /
            (Math.pow(1 + apr, tenure) - 1);

          if (calculatedEMI > emi) {
            upperRate = apr;
          } else {
            lowerRate = apr;
          }
        }

        return apr * 12 * 100; // Convert to annual percentage rate
      };

      const calculatedAPR = calculateAPR(
        emiAmount,
        netLoanAmount,
        loanTenureMonths
      );
      setApr(isNaN(calculatedAPR) ? 0 : Math.round(calculatedAPR));
    };
    const amortizationSchedule = calculateAmortizationSchedule();

    const formData = {
      totalPrincipal: formatter(loanAmount),
      loanAmount: formatter(loanAmount),
      emi: formatter(emi),
      processingFeeAmount: formatter(processingFeeAmount),
      totalLoanPayments: formatter(totalRepaymentAmount),
      totalInterest: formatter(totalInterestPayable),
      apr,
      totalCost: formatter(totalCostOfLoan),
      loanClosureDate: calculateLoanClosureDate(),
      amortizationSchedule,
    };
    onData(formData);

    handleCalculate();
  }, [
    loanAmount,
    interestRate,
    loanTenureMonths,
    processingFeePercent,
    fixedFee,
    emi,
    processingFeeAmount,
    totalRepaymentAmount,
    totalInterestPayable,
    apr,
    addOrginationOpen,
    processingFeeOption,
    calculateLoanClosureDate,
    calculateAmortizationSchedule,
    totalCostOfLoan,
  ]);

  const formatter = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0, // Ensure no decimals
      maximumFractionDigits: 0, // Ensure no decimals
    }).format(value);
  };

  const formatCurrency = (value) => {
    return value.toLocaleString("en-IN");
  };

  const handleAmountChange = (e, setter) => {
    const value = e.target.value.replace(/,/g, ""); // Remove commas
    if (/^\d*\.?\d*$/.test(value)) {
      const numericValue = Number(value); // Convert to number
      if (numericValue <= 5000000) {
        // Restrict to maximum 50,00,000
        setter(numericValue);
      }
    }
  };
  return (
    <>
      <div
        id="loan_details__container"
        className=" loan_details__content__box rounded py-4 px-3"
      >
        <Typography
          sx={{
            fontSize: "16px",
            lineHeight: "24px",

            fontFamily: "'Archivo', sans-serif",
          }}
          className="px-2 fw-bold"
        >
          Loan details
        </Typography>
        <div className="d-flex flex-column gap-1 mt-3">
          <div className="details_box p-2">
            <p style={inputLableStyle} className="mb-1">
              Loan amount
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <span className="archivo">&#8377;</span>
                  </InputAdornment>
                }
                value={formatCurrency(loanAmount)} // Display value with commas
                onChange={(e) => handleAmountChange(e, setLoanAmount)}
              />
            </FormControl>
          </div>
          <div className="details_box p-2">
            <p style={inputLableStyle} className="mb-1">
              Interest rate
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                sx={{
                  fontFamily: "'Archivo',sans-serif",
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <span className="archivo">%</span>
                  </InputAdornment>
                }
                value={interestRate || 0}
                onChange={(e) => {
                  let value = e.target.value.replace(/,/g, ""); // Remove commas

                  if (/^\d*\.?\d*$/.test(value)) {
                    if (value === "") {
                      setInterestRate(""); // Allow empty field temporarily
                    } else {
                      setInterestRate(value.replace(/^0+/, "") || "0"); // Remove leading zero
                    }
                  }
                }}
                onFocus={() => interestRate === "0" && setInterestRate("")} // Empty field on focus
                onBlur={() =>
                  setInterestRate(interestRate === "" ? "0" : interestRate)
                }
              />
            </FormControl>
          </div>
          <div className="details_box  p-2">
            <p style={inputLableStyle} className="mb-1">
              Loan term (months)
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                value={loanTenureMonths}
                onChange={(e) => handleAmountChange(e, setLoanTenureMonths)}
              />
            </FormControl>
          </div>
          <div className="details_box p-2">
            <p style={inputLableStyle} className="mb-1">
              Start date
            </p>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={["month", "year"]}
                format="MM/YYYY"
                name="startDate"
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    height: "40px",
                    border: "1.5px solid #b783cc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 10px",
                    border: "none",
                  },
                }}
                value={startDate}
                onChange={(newDate) =>
                  setStartDate(newDate ? dayjs(newDate) : null)
                }
              />
            </LocalizationProvider>
          </div>
          <div className="px-2 mt-3">
            <Typography
              className="archivo"
              sx={{
                fontSize: "16px",
                lineHeight: "24px",
                fontWeight: 700,
                color: "#59266D",
                fontFamily: "'Archivo', sans-serif",
              }}
              onClick={handleOrigination}
            >
              Add Processing Fee &nbsp;
              <span>
                {" "}
                <FontAwesomeIcon
                  icon={addOrginationOpen ? faChevronUp : faChevronDown}
                  style={{ fontSize: 16 }}
                />{" "}
              </span>
            </Typography>
            <div
              className={`origination_details ${
                addOrginationOpen === true ? "d-flex" : "d-none"
              }  flex-column gap-1 mt-4`}
            >
              <div className="details_box py-2">
                <p style={inputLableStyle} className="mb-1">
                  How are processing fees calculated?
                </p>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  name="radio-buttons-group"
                  className="gap-2"
                  value={processingFeeOption}
                  onChange={handleProcessingFeeOpion}
                >
                  <div
                    style={{
                      width: "100%",
                      border: "1.5px solid #b783cc",
                      padding: "0.1px 2px 0.1px 0",
                      backgroundColor: "#fff",
                      borderRadius: "4px",
                    }}
                  >
                    <FormControlLabel
                      value="percentage"
                      sx={{ paddingLeft: "13px" }}
                      control={<Radio size="small" />}
                      label={
                        <span className="radio_label text-break archivo">
                          Percentage
                        </span>
                      }
                    />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      border: "1.5px solid #b783cc",
                      padding: "0.1px 2px 0.1px 0",
                      backgroundColor: "#fff",
                      borderRadius: "4px",
                    }}
                  >
                    <FormControlLabel
                      value="fixedAmount"
                      sx={{ paddingLeft: "13px" }}
                      control={<Radio size="small" />}
                      label={
                        <span className="radio_label text-break archivo">
                          Fixed amount
                        </span>
                      }
                    />
                  </div>
                </RadioGroup>
              </div>

              <div className="details_box  py-2">
                <p style={inputLableStyle} className="mb-1">
                  Processing fee
                </p>
                <FormControl fullWidth sx={formControlStyle}>
                  {processingFeeOption === "fixedAmount" ? (
                    <OutlinedInput
                      id="outlined-adornment-amount"
                      fullWidth
                      size="small"
                      startAdornment={
                        <InputAdornment position="start">
                          <span className="archivo">&#8377;</span>
                        </InputAdornment>
                      }
                      value={formatCurrency(fixedFee || "")}
                      onChange={(e) => handleAmountChange(e, setFixedFee)}
                    />
                  ) : (
                    <OutlinedInput
                      id="outlined-adornment-amount"
                      fullWidth
                      size="small"
                      endAdornment={
                        <InputAdornment position="end">
                          <span className="archivo">%</span>
                        </InputAdornment>
                      }
                      value={processingFeePercent || 0}
                      onChange={(e) => {
                        let value = e.target.value.replace(/,/g, ""); // Remove commas

                        if (/^\d*\.?\d*$/.test(value)) {
                          if (value === "") {
                            setProcessingFeePercent(""); // Allow empty field temporarily
                          } else {
                            setProcessingFeePercent(
                              value.replace(/^0+/, "") || "0"
                            ); // Remove leading zero
                          }
                        }
                      }}
                      onFocus={() =>
                        processingFeePercent === "0" &&
                        setProcessingFeePercent("")
                      } // Empty field on focus
                      onBlur={() =>
                        setProcessingFeePercent(
                          processingFeePercent === ""
                            ? "0"
                            : processingFeePercent
                        )
                      }
                    />
                  )}
                </FormControl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanDetails;
